import { Button } from '@design/components/button/Button';
import { Icon } from '@design/components/icon/Icon';
import { Tooltip, TooltipTrigger } from '@design/components/tooltip/Tooltip';
import Logo from '@static/images/brand/favicon.svg';
import { RenderFeatureFlag } from '@terminal/components/render-feature-flag/RenderFeatureFlag';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { useMobileNavLayout } from '@terminal/hooks/use-mobile-nav-layout/useMobileNavLayout';
import { useListScheduledQueries } from '@terminal/react-query-hooks/alfred/useScheduledQueries';
import { appPaths } from '@terminal/routes/app-paths';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { useHistorySidebarNavigation } from '@terminal/stores/use-history-sidebar-navigation/useHistorySidebarNavigation';
import { useScheduleDrawer } from '@terminal/stores/use-schedule-drawer/useScheduleDrawer';
import { NewChatButton } from '@terminal/views/alfred/new-chat-button/NewChatButton';
import { useThinkingStepsSidebar } from '@terminal/views/alfred/research/hooks/useThinkingStepsSidebar';
import { PersonalizationDrawer } from '@terminal/views/alfred/research/personalization-drawer/PersonalizationDrawer';
import { useChatModeStore } from '@terminal/views/alfred/use-chat-navigation/useChatNavigation';
import clsx from 'clsx';
import { memo, ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { HistoryGroup, orderedGroupTypes } from '../history-group/HistoryGroup';
import { HistoryGroupLoader } from '../history-group-loader/HistoryGroupLoader';
import { useFetchInfiniteAllConversations } from '../hooks/useFetchInfiniteConversation';
import { useReasonerParams } from '../hooks/useReasonerParams';
import { SearchChat } from '../search-chat/SearchChat';
import { useChat } from '../use-chat/useChat';
import { useChatSettings } from '../use-chat-settings/useChatSettings';
import { CategoryTabs } from './CategoryTabs';
import { ConnectMicrosoft } from './ConnectMicrosoft';
import styles from './HistorySidebar.module.scss';
import { ScheduledTaskList } from './ScheduledTaskList';
import { groupConversations } from './utils/history-sidebar-utils';

export type { CategoryType } from '@terminal/stores/use-history-sidebar-navigation/useHistorySidebarNavigation';

export const MAX_SCHEDULED_TASKS = 3;

interface HistorySidebarProps {
  isHistoryExpanded: boolean;
  toggleHistoryTab: () => void;
  handleOpenSharingModal: () => void;
  headerActions?: ReactNode;
}

export const HistorySidebarComponent = ({
  isHistoryExpanded,
  toggleHistoryTab,
  handleOpenSharingModal,
  headerActions,
}: HistorySidebarProps) => {
  const navigate = useNavigate();
  const contentSectionRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(['chat', 'common']);
  const { trackAlfredChatSearchOpen } = usePosthogEvents();
  const [isSearchOpened, setSearchOpen] = useState(false);
  const { category, setCategory, setSelectedScheduledQueryId } =
    useHistorySidebarNavigation();
  const { conversations, clearActiveConversation } = useChat(state => ({
    conversations: state.conversations,
    clearActiveConversation: state.clearActiveConversation,
  }));

  const isMobileNavLayout = useMobileNavLayout();

  const { displayPersonalizationDrawer, setDisplayPersonalizationDrawer } =
    useChatSettings(state => ({
      displayPersonalizationDrawer: state.displayPersonalizationDrawer,
      setDisplayPersonalizationDrawer: state.setDisplayPersonalizationDrawer,
    }));

  const { deleteReasonerConversationId } = useReasonerParams();
  const resetSidebar = useThinkingStepsSidebar(state => state.resetSidebar);
  const setNewConversationSource = useChatModeStore(
    state => state.setNewConversationSource
  );

  const handleNewConversation = () => {
    setNewConversationSource(POSTHOG_TRACKING_SOURCES.ALFRED_SIDE_MENU);
    deleteReasonerConversationId();
    clearActiveConversation();
    resetSidebar();
    setSelectedScheduledQueryId(null);
    navigate(appPaths.alfred);
  };

  const openScheduleDrawer = useScheduleDrawer(state => state.open);

  const isScheduledTaskEnabled = useFlags(state =>
    state.getFeatureFlagState('scheduled_task_feature')
  );

  const { data: scheduledQueriesData } = useListScheduledQueries(
    { status: 'active' },
    isScheduledTaskEnabled
  );
  const scheduledTasksCount = scheduledQueriesData?.total_count ?? 0;
  const isScheduleButtonDisabled =
    isScheduledTaskEnabled && scheduledTasksCount >= MAX_SCHEDULED_TASKS;

  const starredConversations = conversations
    .filter(({ favourite }) => favourite)
    .sort((a, b) => (a.date > b.date ? -1 : 1));
  const groups = groupConversations(
    conversations.sort((a, b) => (a.date > b.date ? -1 : 1))
  );

  const groupKeys = orderedGroupTypes.filter(
    groupType => groups[groupType]?.length > 0
  );

  //TODO: will kill this next and move everything to store
  const { isLoading, hasNextPage, fetchNextPage } =
    useFetchInfiniteAllConversations();

  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = contentSectionRef.current;
    const sentinel = loadMoreSentinelRef.current;

    if (!root || !sentinel || !hasNextPage || isLoading || !isHistoryExpanded) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          fetchNextPage();
        }
      },
      { root, rootMargin: '20px', threshold: 0 }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [isHistoryExpanded, hasNextPage, isLoading, fetchNextPage]);

  return (
    <>
      <div
        className={clsx(
          styles.backdrop,
          isHistoryExpanded && styles.isExpanded
        )}
        onClick={toggleHistoryTab}
      />
      <div
        className={clsx(styles.root, isHistoryExpanded && styles.isExpanded)}
      >
        <div className={styles.toggleButtonWrapper}>
          {isHistoryExpanded && (
            <header className={styles.header}>
              {t('common:sideNavigation.alfred')}
            </header>
          )}
          <div className={styles.leftSection}>
            <TooltipContainer
              label={
                isHistoryExpanded
                  ? t('common:sideNavigation.hideMenu')
                  : t('common:sideNavigation.showMenu')
              }
              trigger={TooltipTrigger.Hover}
              disabled={isMobileNavLayout}
              placement="right"
            >
              <button
                className={clsx(
                  styles.toggleButton,
                  isHistoryExpanded && styles.isExpanded
                )}
                onClick={toggleHistoryTab}
                data-testid={'button-toggle'}
              >
                <Icon
                  iconName={
                    isMobileNavLayout && !isHistoryExpanded
                      ? 'HamburgerMenu'
                      : 'ArrowRight'
                  }
                  size={isMobileNavLayout ? 20 : 16}
                />
              </button>
            </TooltipContainer>
            {!isHistoryExpanded && isMobileNavLayout && (
              <div className={styles.logoWrapper}>
                <Logo />
              </div>
            )}
          </div>
          {!isHistoryExpanded && isMobileNavLayout && (
            <div className={styles.headerActions}>
              {headerActions}
              <NewChatButton
                iconOnly
                size="small"
                isHistoryExpanded={isHistoryExpanded}
                onClick={handleNewConversation}
              />
            </div>
          )}
        </div>
        {isHistoryExpanded && (
          <div className={styles.mobileButtons}>
            <div
              className={styles.buttonWrapper}
              onClick={handleNewConversation}
            >
              <NewChatButton iconOnly isHistoryExpanded={isHistoryExpanded} />
              {isHistoryExpanded && t('chat:historySidebar.newConversation')}
            </div>
          </div>
        )}
        <div
          className={clsx(
            styles.headerSection,
            isHistoryExpanded && styles.isExpanded
          )}
        >
          <div
            className={clsx(
              styles.buttonsWrapper,
              isHistoryExpanded && styles.isExpanded
            )}
          >
            <div
              className={styles.buttonWrapper}
              onClick={handleNewConversation}
            >
              <NewChatButton iconOnly isHistoryExpanded={isHistoryExpanded} />
              {isHistoryExpanded && t('chat:historySidebar.newConversation')}
            </div>
            <div
              className={styles.buttonWrapper}
              onClick={() => {
                if (!isSearchOpened) {
                  trackAlfredChatSearchOpen(POSTHOG_TRACKING_SOURCES.ALFRED);
                }

                setSearchOpen(!isSearchOpened);
              }}
            >
              <SearchChat
                isHistoryExpanded={isHistoryExpanded}
                setIsOpen={setSearchOpen}
                isOpen={isSearchOpened}
              />
              {isHistoryExpanded && t('chat:historySidebar.searchConversation')}
            </div>
            <div
              className={styles.buttonWrapper}
              onClick={() => {
                setDisplayPersonalizationDrawer(true);
              }}
            >
              <Tooltip
                className={styles.settingsTooltip}
                placement={'right'}
                label={t('settings.tooltipLabel')}
                disabled={isHistoryExpanded}
              >
                <Button
                  className={styles.settingsButton}
                  variant="tertiary"
                  size="small"
                  iconName="Settings"
                  data-testid="settings-button"
                />
              </Tooltip>
              {isHistoryExpanded && t('chat:settings.title')}
            </div>
            {isScheduledTaskEnabled && (
              <TooltipContainer
                label={
                  isScheduleButtonDisabled
                    ? t('chat:historySidebar.scheduleTaskDisabledMaxReached')
                    : t('chat:historySidebar.scheduleTask')
                }
                inPortal
                trigger={[TooltipTrigger.Hover]}
              >
                <div
                  className={clsx(
                    styles.buttonWrapper,
                    isScheduleButtonDisabled && styles.disabled
                  )}
                  onClick={() =>
                    !isScheduleButtonDisabled && openScheduleDrawer()
                  }
                  data-testid="schedule-task-button"
                >
                  <div className={styles.scheduleTaskContent}>
                    <Button
                      className={styles.scheduleTaskButton}
                      variant="tertiary"
                      size="small"
                      iconName="Clock"
                      disabled={isScheduleButtonDisabled}
                    />
                    {isHistoryExpanded && (
                      <>
                        {t('chat:historySidebar.scheduleTask')}
                        <span className={styles.scheduleTaskBadge}>
                          {scheduledTasksCount}/{MAX_SCHEDULED_TASKS}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </TooltipContainer>
            )}
            <RenderFeatureFlag requiredFlag={'msf_connection'}>
              <ConnectMicrosoft isHistoryExpanded={isHistoryExpanded} />
            </RenderFeatureFlag>
          </div>
        </div>

        {isHistoryExpanded && (
          <>
            <CategoryTabs category={category} changeCategory={setCategory} />
            <div className={styles.contentSection} ref={contentSectionRef}>
              {category === 'bookmarked' && (
                <HistoryGroup
                  groupType="starred"
                  conversations={starredConversations}
                  category={category}
                  handleOpenSharingModal={handleOpenSharingModal}
                />
              )}
              {isScheduledTaskEnabled && category === 'scheduled' && (
                <ScheduledTaskList />
              )}
              {category === 'all' && (
                <>
                  {groupKeys.map(groupType => (
                    <HistoryGroup
                      key={groupType}
                      groupType={groupType}
                      conversations={groups[groupType]}
                      handleOpenSharingModal={handleOpenSharingModal}
                      category={category}
                    />
                  ))}
                </>
              )}
              {hasNextPage && (
                <div
                  className={styles.loadMoreSentinel}
                  ref={loadMoreSentinelRef}
                  data-testid="history-load-more-sentinel"
                />
              )}
              {isLoading && <HistoryGroupLoader />}
            </div>
          </>
        )}
        {displayPersonalizationDrawer && <PersonalizationDrawer />}
      </div>
    </>
  );
};

export const HistorySidebar = memo(HistorySidebarComponent);
